import type { CrmCopilotRequest, CrmCopilotResponse } from '../types/CrmCopilot';

const getAiBaseUrl = () => process.env.REACT_APP_AI_BACKEND_BASE_URL;

const ensureJsonResponse = async (response: Response) => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `CRM copilot API error: ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const body = await response.text();
    throw new Error(
      `CRM copilot API returned non-JSON response (content-type: ${contentType || 'unknown'}). ` +
        `Response starts with: ${JSON.stringify(body.slice(0, 80))}`
    );
  }

  return response.json();
};

const getCrmBearerToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('You are not logged in. Please sign in again to use Sales Copilot.');
  }
  return token;
};

export const sendCrmCopilotRequest = async (payload: CrmCopilotRequest): Promise<CrmCopilotResponse> => {
  const aiBaseUrl = getAiBaseUrl();
  if (!aiBaseUrl) {
    throw new Error(
      [
        'Missing REACT_APP_AI_BACKEND_BASE_URL.',
        'Create query-app/.env (copy from query-app/.env.example) and restart the dev server.',
        'Example: REACT_APP_AI_BACKEND_BASE_URL=http://localhost:3000',
      ].join(' ')
    );
  }

  const endpoint = `${aiBaseUrl.replace(/\/$/, '')}/crm/chat`;
  let response: Response;

  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getCrmBearerToken()}`,
        'X-Client-Platform': 'query-app',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown network error';
    throw new Error(
      `Unable to reach Sales Copilot at ${endpoint}. ` +
        `Check that ai-backend is running and that its CORS allows http://localhost:3000. ` +
        `Underlying error: ${message}`
    );
  }

  return ensureJsonResponse(response) as Promise<CrmCopilotResponse>;
};
